import * as React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff } from "lucide-react"; // Import eye icons
import {
  useAddInstructorMutation,
  useUpdateInstructorMutation,
} from "@/store/instructorApi";

// Instructor Form component for both creation and editing
const InstructorForm = ({ instructorData, onClose, mode = "create" }) => {
  const [registerInstructor] = useAddInstructorMutation();
  const [updateInstructor] = useUpdateInstructorMutation();

  const [formData, setFormData] = React.useState({
    name: instructorData?.name || "",
    email: instructorData?.email || "",
    password: "",
    role: "instructor",
  });

  // State to toggle password visibility
  const [showPassword, setShowPassword] = React.useState(false);

  const [errors, setErrors] = React.useState({});
  const [status, setStatus] = React.useState({ message: "", type: "" });

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }

    if (mode === "create" && !formData.password) {
      newErrors.password = "Password is required";
    } else if (formData.password && formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ message: "", type: "" });

    if (validateForm()) {
      try {
        if (mode === "edit") {
          const updateData = {
            ...formData,
            id: instructorData._id,
          };
          if (!formData.password) {
            delete updateData.password;
          }
          await updateInstructor(updateData).unwrap();
          setStatus({
            type: "success",
            message: "Instructor updated successfully!",
          });
        } else {
          await registerInstructor(formData).unwrap();
          setStatus({
            type: "success",
            message: "Instructor registered successfully!",
          });
        }

        setTimeout(() => {
          onClose();
        }, 1500);
      } catch (error) {
        setStatus({
          type: "error",
          message: error?.message || "Operation failed",
        });
      }
    }
  };

  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader>
        <CardTitle className="text-2xl font-bold text-center">
          <h1>
            {mode === "edit" ? "Edit Instructor" : "Instructor Registration"}
          </h1>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input
              id="name"
              name="name"
              type="text"
              placeholder="Enter name"
              value={formData.name}
              onChange={handleInputChange}
              className={errors.name ? "border-red-500" : ""}
            />
            {errors.name && (
              <p className="text-sm text-red-500">{errors.name}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="Enter email"
              value={formData.email}
              autoComplete="off"
              onChange={handleInputChange}
              className={errors.email ? "border-red-500" : ""}
            />
            {errors.email && (
              <p className="text-sm text-red-500">{errors.email}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">
              Password{" "}
              {mode === "edit" && "(Leave blank to keep current password)"}
            </Label>
            <div className="relative">
              <Input
                id="password"
                name="password"
                type={showPassword ? "text" : "password"}
                placeholder={
                  mode === "edit"
                    ? "Enter new password (optional)"
                    : "Enter password"
                }
                value={formData.password}
                onChange={handleInputChange}
                className={errors.password ? "border-red-500 pr-10" : "pr-10"}
              />
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 focus:outline-none"
              >
                {showPassword ? (
                  <EyeOff size={18} className="text-gray-500" />
                ) : (
                  <Eye size={18} className="text-gray-500" />
                )}
              </button>
            </div>
            {errors.password && (
              <p className="text-sm text-red-500">{errors.password}</p>
            )}
          </div>

          {status.message && (
            <Alert
              className={`mb-4 ${
                status.type === "success" ? "bg-red-50" : "bg-red-50"
              }`}
            >
              <AlertDescription>{status.message}</AlertDescription>
            </Alert>
          )}

          <Button type="submit" className="w-full">
            {mode === "edit" ? "Update" : "Register"}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default InstructorForm;
